import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL, ENDPOINTS, MAX_BULK_UPLOAD_FILES, SUPPORTED_RESUME_FORMATS } from './config';

export default function BulkUploadApplicants() {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [extractingFields, setExtractingFields] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('upload'); // 'upload', 'extracting', 'editing', 'submitting'
  const [extractedData, setExtractedData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [successCount, setSuccessCount] = useState(0);
  const [failureCount, setFailureCount] = useState(0);
  const [processingResults, setProcessingResults] = useState([]);

  // Configuration constants are now imported from config file

  //TODO: This can become slow when we fetch all job postings every time. We should create a separate endpoint for fetching JobPosting details.
  // Add useEffect to fetch job details
  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}${ENDPOINTS.JOBS}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Failed to fetch");
        
        // Find the specific job
        const job = data.find(j => j.job_id === jobId);
        if (!job) throw new Error("Job posting not found");
        
        setJobDetails(job);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchJobDetails();
  }, [jobId]);

  // Handle multiple file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > MAX_BULK_UPLOAD_FILES) {
      setError(`Maximum ${MAX_BULK_UPLOAD_FILES} files allowed for bulk upload`);
      return;
    }

    // Validate file types
    const invalidFiles = files.filter(file => {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      return !SUPPORTED_RESUME_FORMATS.includes(extension);
    });

    if (invalidFiles.length > 0) {
      setError(`Invalid file formats detected. Only ${SUPPORTED_RESUME_FORMATS.join(', ')} are allowed.`);
      return;
    }

    setSelectedFiles(files);
    setError(null);
    setExtractedData([]);
    setCurrentStep('upload');
  };

  // Handle bulk field extraction
  const handleBulkExtraction = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select resume files first');
      return;
    }

    setExtractingFields(true);
    setCurrentStep('extracting');
    setExtractionProgress(0);
    setError(null);

    const results = [];
    
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('resume', file);

        try {
          const response = await fetch(`${API_BASE_URL}${ENDPOINTS.EXTRACT_FIELDS}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          const result = await response.json();
          
          if (!response.ok) {
            throw new Error(result.error || 'Failed to extract fields');
          }

          const extractedData = result.data;
          results.push({
            id: `temp-${i}`,
            fileName: file.name,
            file: file,
            first_name: extractedData.first_name || '',
            last_name: extractedData.last_name || '',
            email: extractedData.email || '',
            skills: extractedData.skills ? extractedData.skills.split(',').map(s => s.trim()) : [],
            skillsText: extractedData.skills || '',
            experience_years: extractedData.experience_years?.toString() || '',
            education: extractedData.education || '',
            location: extractedData.location || '',
            notes: '',
            resume_text: '',
            extractionStatus: 'success'
          });
        } catch (err) {
          results.push({
            id: `temp-${i}`,
            fileName: file.name,
            file: file,
            first_name: '',
            last_name: '',
            email: '',
            skills: [],
            skillsText: '',
            experience_years: '',
            education: '',
            location: '',
            notes: '',
            resume_text: '',
            extractionStatus: 'failed',
            extractionError: err.message
          });
        }

        // Update progress
        setExtractionProgress(((i + 1) / selectedFiles.length) * 100);
      }

      setExtractedData(results);
      setCurrentStep('editing');
    } catch (err) {
      setError(err.message);
    } finally {
      setExtractingFields(false);
    }
  };

  // Handle field editing
  const handleFieldEdit = (rowId, field, value) => {
    setExtractedData(prev => 
      prev.map(row => {
        if (row.id === rowId) {
          if (field === 'skillsText') {
            // Update both skillsText and skills array
            return {
              ...row,
              skillsText: value,
              skills: value.split(',').map(skill => skill.trim()).filter(skill => skill.length > 0)
            };
          }
          return {
            ...row,
            [field]: value
          };
        }
        return row;
      })
    );
  };

  // Handle bulk submission
  const handleBulkSubmit = async () => {
    const validData = extractedData.filter(row => 
      row.first_name.trim() && 
      row.last_name.trim() && 
      row.email.trim() && 
      row.extractionStatus === 'success'
    );

    if (validData.length === 0) {
      setError('No valid applicants to submit. Please ensure all required fields are filled.');
      return;
    }

    setSubmitting(true);
    setCurrentStep('submitting');
    setSubmissionProgress(0);
    setSuccessCount(0);
    setFailureCount(0);
    setProcessingResults([]);
    setError(null);

    const results = [];

    try {
      for (let i = 0; i < validData.length; i++) {
        const applicant = validData[i];
        const token = localStorage.getItem('token');
        const formData = new FormData();

        // Add all form fields to FormData
        formData.append('first_name', applicant.first_name);
        formData.append('last_name', applicant.last_name);
        formData.append('email', applicant.email);
        formData.append('skills', JSON.stringify(applicant.skills));
        formData.append('experience_years', applicant.experience_years);
        formData.append('education', applicant.education);
        formData.append('location', applicant.location);
        formData.append('notes', applicant.notes);
        formData.append('resume_text', applicant.resume_text);
        formData.append('resume', applicant.file);

        try {
          const response = await fetch(`${API_BASE_URL}${ENDPOINTS.APPLICANTS(jobId)}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          const data = await response.json();
          
          if (!response.ok) {
            throw new Error(data.error || 'Failed to add applicant');
          }

          results.push({
            fileName: applicant.fileName,
            name: `${applicant.first_name} ${applicant.last_name}`,
            status: 'success',
            message: 'Successfully added'
          });
          setSuccessCount(prev => prev + 1);
        } catch (err) {
          results.push({
            fileName: applicant.fileName,
            name: `${applicant.first_name} ${applicant.last_name}`,
            status: 'failed',
            message: err.message
          });
          setFailureCount(prev => prev + 1);
        }

        // Update progress
        setSubmissionProgress(((i + 1) / validData.length) * 100);
        setProcessingResults([...results]);
      }

      // Show completion message
      if (results.filter(r => r.status === 'success').length > 0) {
        // Navigate back with success message after a short delay
        setTimeout(() => {
          navigate('/jd', {
            state: {
              showJobId: jobId,
              bulkUploadComplete: true,
              successCount: results.filter(r => r.status === 'success').length,
              failureCount: results.filter(r => r.status === 'failed').length,
              jobTitle: jobDetails.title
            }
          });
        }, 2000);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Remove a row from the extracted data
  const handleRemoveRow = (rowId) => {
    setExtractedData(prev => prev.filter(row => row.id !== rowId));
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          {/* Job Details Header */}
          {jobDetails && (
            <div className="card mb-4">
              <div className="card-body">
                <h4 className="card-title">Bulk Upload Applicants for:</h4>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h5 className="text-primary mb-2">{jobDetails.title}</h5>
                    <p className="text-muted mb-1">
                      <i className="bi bi-geo-alt"></i> {jobDetails.location}
                    </p>
                  </div>
                  <span className={`badge ${jobDetails.status === 'open' ? 'bg-success' : 'bg-secondary'}`}>
                    {jobDetails.status.charAt(0).toUpperCase() + jobDetails.status.slice(1)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Progress Steps */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className={`d-flex align-items-center ${currentStep === 'upload' ? 'text-primary' : currentStep === 'extracting' || currentStep === 'editing' || currentStep === 'submitting' ? 'text-success' : 'text-muted'}`}>
                  <div className={`rounded-circle d-flex align-items-center justify-content-center me-2 ${currentStep === 'upload' ? 'bg-primary text-white' : currentStep === 'extracting' || currentStep === 'editing' || currentStep === 'submitting' ? 'bg-success text-white' : 'bg-light'}`} style={{ width: '32px', height: '32px' }}>
                    1
                  </div>
                  <span>Upload Files</span>
                </div>
                <div className={`d-flex align-items-center ${currentStep === 'extracting' ? 'text-primary' : currentStep === 'editing' || currentStep === 'submitting' ? 'text-success' : 'text-muted'}`}>
                  <div className={`rounded-circle d-flex align-items-center justify-content-center me-2 ${currentStep === 'extracting' ? 'bg-primary text-white' : currentStep === 'editing' || currentStep === 'submitting' ? 'bg-success text-white' : 'bg-light'}`} style={{ width: '32px', height: '32px' }}>
                    2
                  </div>
                  <span>Extract Data</span>
                </div>
                <div className={`d-flex align-items-center ${currentStep === 'editing' ? 'text-primary' : currentStep === 'submitting' ? 'text-success' : 'text-muted'}`}>
                  <div className={`rounded-circle d-flex align-items-center justify-content-center me-2 ${currentStep === 'editing' ? 'bg-primary text-white' : currentStep === 'submitting' ? 'bg-success text-white' : 'bg-light'}`} style={{ width: '32px', height: '32px' }}>
                    3
                  </div>
                  <span>Review & Edit</span>
                </div>
                <div className={`d-flex align-items-center ${currentStep === 'submitting' ? 'text-primary' : 'text-muted'}`}>
                  <div className={`rounded-circle d-flex align-items-center justify-content-center me-2 ${currentStep === 'submitting' ? 'bg-primary text-white' : 'bg-light'}`} style={{ width: '32px', height: '32px' }}>
                    4
                  </div>
                  <span>Submit</span>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Step 1: File Upload */}
          {currentStep === 'upload' && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Upload Resume Files</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Select Resume Files</label>
                  <input
                    type="file"
                    className="form-control"
                    multiple
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                  <small className="form-text text-muted">
                    Select up to {MAX_BULK_UPLOAD_FILES} resume files. Accepted formats: PDF, DOC, DOCX (Max size: 5MB each)
                  </small>
                </div>

                {selectedFiles.length > 0 && (
                  <div className="mb-3">
                    <h6>Selected Files ({selectedFiles.length}):</h6>
                    <ul className="list-group">
                      {selectedFiles.map((file, index) => (
                        <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          <span>{file.name}</span>
                          <span className="badge bg-secondary">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(`/jd`, { state: { showJobId: jobId } })}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleBulkExtraction}
                    disabled={selectedFiles.length === 0}
                  >
                    Extract Information
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Extraction Progress */}
          {currentStep === 'extracting' && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Extracting Information from Resumes</h5>
              </div>
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <div className="spinner-border text-primary me-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="flex-grow-1">
                    <div className="progress">
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${extractionProgress}%` }}
                        aria-valuenow={extractionProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        {Math.round(extractionProgress)}%
                      </div>
                    </div>
                    <small className="text-muted">
                      Processing {selectedFiles.length} resume files...
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review and Edit */}
          {currentStep === 'editing' && extractedData.length > 0 && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Review and Edit Extracted Information</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th>Skills</th>
                        <th>Experience (Years)</th>
                        <th>Education</th>
                        <th>Location</th>
                        <th>Notes</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extractedData.map((row, index) => (
                        <tr key={row.id} className={row.extractionStatus === 'failed' ? 'table-warning' : ''}>
                          <td>{row.fileName}</td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={row.first_name}
                              onChange={(e) => handleFieldEdit(row.id, 'first_name', e.target.value)}
                              disabled={row.extractionStatus === 'failed'}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={row.last_name}
                              onChange={(e) => handleFieldEdit(row.id, 'last_name', e.target.value)}
                              disabled={row.extractionStatus === 'failed'}
                            />
                          </td>
                          <td>
                            <input
                              type="email"
                              className="form-control form-control-sm"
                              value={row.email}
                              onChange={(e) => handleFieldEdit(row.id, 'email', e.target.value)}
                              disabled={row.extractionStatus === 'failed'}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={row.skillsText}
                              onChange={(e) => handleFieldEdit(row.id, 'skillsText', e.target.value)}
                              placeholder="JavaScript, React, Node.js"
                              disabled={row.extractionStatus === 'failed'}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={row.experience_years}
                              onChange={(e) => handleFieldEdit(row.id, 'experience_years', e.target.value)}
                              min="0"
                              disabled={row.extractionStatus === 'failed'}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={row.education}
                              onChange={(e) => handleFieldEdit(row.id, 'education', e.target.value)}
                              disabled={row.extractionStatus === 'failed'}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={row.location}
                              onChange={(e) => handleFieldEdit(row.id, 'location', e.target.value)}
                              disabled={row.extractionStatus === 'failed'}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={row.notes}
                              onChange={(e) => handleFieldEdit(row.id, 'notes', e.target.value)}
                              disabled={row.extractionStatus === 'failed'}
                            />
                          </td>
                          <td>
                            <span className={`badge ${row.extractionStatus === 'success' ? 'bg-success' : 'bg-danger'}`}>
                              {row.extractionStatus === 'success' ? 'Ready' : 'Failed'}
                            </span>
                            {row.extractionStatus === 'failed' && (
                              <small className="text-danger d-block">{row.extractionError}</small>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleRemoveRow(row.id)}
                              title="Remove this applicant"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    <span className="text-muted">
                      {extractedData.filter(row => row.extractionStatus === 'success').length} of {extractedData.length} applicants ready for submission
                    </span>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setCurrentStep('upload');
                        setExtractedData([]);
                      }}
                    >
                      Back to Upload
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleBulkSubmit}
                      disabled={extractedData.filter(row => row.extractionStatus === 'success').length === 0}
                    >
                      Submit All Applicants
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Submission Progress */}
          {currentStep === 'submitting' && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">Submitting Applicants</h5>
              </div>
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <div className="spinner-border text-primary me-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="flex-grow-1">
                    <div className="progress">
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${submissionProgress}%` }}
                        aria-valuenow={submissionProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        {Math.round(submissionProgress)}%
                      </div>
                    </div>
                    <small className="text-muted">
                      Success: {successCount} | Failed: {failureCount}
                    </small>
                  </div>
                </div>

                {/* Processing Results */}
                {processingResults.length > 0 && (
                  <div className="mt-4">
                    <h6>Processing Results:</h6>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>File Name</th>
                            <th>Applicant Name</th>
                            <th>Status</th>
                            <th>Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {processingResults.map((result, index) => (
                            <tr key={index}>
                              <td>{result.fileName}</td>
                              <td>{result.name}</td>
                              <td>
                                <span className={`badge ${result.status === 'success' ? 'bg-success' : 'bg-danger'}`}>
                                  {result.status}
                                </span>
                              </td>
                              <td>{result.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {submissionProgress === 100 && (
                  <div className="alert alert-success mt-3">
                    <h5>Bulk Upload Complete!</h5>
                    <p>Successfully processed {successCount} applicants. {failureCount > 0 && `${failureCount} failed.`}</p>
                    <p>Redirecting to job dashboard...</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 